import { useState, useMemo, useEffect } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAppContext } from "../context";
import { api } from "../api";
import {
  Trophy,
  Loader2,
  AlertTriangle,
  Medal,
  ExternalLink,
  Crown,
  Info,
  ChevronDown,
  DollarSign,
  ArrowUpDown,
} from "lucide-react";
import { cn } from "../lib/utils";

const BUYBACK_TOGGLE_KEY = "cc_show_buyback";
const SORT_KEY = "cc_leaderboard_sort";

type SortOption = "wins" | "pl" | "cost" | "top" | "last";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "wins", label: "Wins" },
  { value: "pl", label: "P/L" },
  { value: "cost", label: "Cost" },
  { value: "top", label: "Top Win" },
  { value: "last", label: "Recent" },
];

interface LeaderboardEntry {
  rank: number;
  wallet: string;
  tierWinCount: number;
  totalPulls: number;
  totalValue: number;
  biggestWin: number;
  totalBuybackValue: number | null;
  biggestBuybackWin: number | null;
  lastWinAt: Date;
}

interface LeaderboardResponse {
  range: { from: string; to: string } | null;
  tierFilter: number[] | null;
  buybackPercentage: number | null;
  totalUniqueWinners: number;
  data: LeaderboardEntry[];
  pagination: {
    hasMore: boolean;
    nextCursor: string | null;
    limit: number;
  };
}

interface TierInfo {
  id: number;
  name: string;
  color: string;
  displayOrder: number;
}

interface MachineStatsResponse {
  machine: {
    id: number;
    code: string;
    name: string;
    priceUsd: number;
  };
  tiers?: TierInfo[];
  stats: {
    totalSpins: number;
  };
}

function shortenWallet(address: string): string {
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(date).toLocaleDateString();
}

function getRankIcon(rank: number) {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return null;
}

function getRankStyle(rank: number) {
  if (rank === 1) return "bg-yellow-500/10 border-yellow-500/30";
  if (rank === 2) return "bg-gray-500/10 border-gray-500/30";
  if (rank === 3) return "bg-amber-500/10 border-amber-500/30";
  return "";
}

export function LeaderboardPage() {
  const { selectedMachine, timeRange } = useAppContext();
  const [selectedTierIds, setSelectedTierIds] = useState<number[]>([]);
  const [showBuyback, setShowBuyback] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(BUYBACK_TOGGLE_KEY);
      return stored === null ? true : stored === "true";
    }
    return true;
  });
  const [sortBy, setSortBy] = useState<SortOption>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(SORT_KEY);
      if (stored && SORT_OPTIONS.some((o) => o.value === stored)) return stored as SortOption;
    }
    return "wins";
  });

  // Persist preferences
  useEffect(() => {
    localStorage.setItem(BUYBACK_TOGGLE_KEY, String(showBuyback));
  }, [showBuyback]);
  useEffect(() => {
    localStorage.setItem(SORT_KEY, sortBy);
  }, [sortBy]);

  // Fetch machine stats for price info and tiers
  const { data: machineData } = useQuery({
    queryKey: ["machine-stats", selectedMachine],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const response = await api.stats.machines({ identifier: selectedMachine }).get();
      if (response.data && "machine" in response.data) {
        return response.data as MachineStatsResponse;
      }
      return null;
    },
    enabled: !!selectedMachine,
    staleTime: 60000,
  });

  // Sort tiers by displayOrder
  const sortedTiers = useMemo(() => {
    if (!machineData?.tiers) return [];
    return [...machineData.tiers].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [machineData?.tiers]);

  // Toggle tier selection
  const toggleTier = (tierId: number) => {
    setSelectedTierIds((prev) =>
      prev.includes(tierId)
        ? prev.filter((id) => id !== tierId)
        : [...prev, tierId]
    );
  };

  // Select/deselect all tiers
  const toggleAllTiers = () => {
    setSelectedTierIds(
      selectedTierIds.length === sortedTiers.length ? [] : sortedTiers.map((t) => t.id)
    );
  };

  // Fetch leaderboard data with tier filter and pagination
  const {
    data: leaderboardData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ["leaderboard", selectedMachine, timeRange, selectedTierIds, sortBy, showBuyback],
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = {
        limit: "25",
      };
      if (selectedMachine) query.machine = selectedMachine;
      if (timeRange !== "all") {
        query.range = timeRange;
        query.tzOffset = String(new Date().getTimezoneOffset());
      }
      if (selectedTierIds.length > 0) query.tiers = selectedTierIds.join(",");
      if (pageParam) query.cursor = pageParam;
      if (sortBy !== "wins") query.sort = sortBy;
      if (showBuyback) query.useBuyback = "true";

      const response = await api.stats.leaderboard.get({ query });
      if (response.data && "data" in response.data) {
        return response.data as LeaderboardResponse;
      }
      throw new Error("Failed to fetch leaderboard");
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 60000, // 1 minute
  });

  const entries = leaderboardData?.pages.flatMap((p) => p?.data ?? []) ?? [];

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md border-destructive/50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to Load Leaderboard</h2>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : "An unexpected error occurred"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Trophy className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <CardTitle>Leaderboard</CardTitle>
              <CardDescription>
                Top players ranked by {SORT_OPTIONS.find((o) => o.value === sortBy)?.label.toLowerCase() ?? "tier wins"}
                {selectedMachine
                  ? ` on ${machineData?.machine.name ?? selectedMachine}`
                  : " across all machines"}
                {timeRange !== "all" && ` (${timeRange})`}
              </CardDescription>
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground mr-2 flex items-center gap-1">
                <ArrowUpDown className="w-3.5 h-3.5" />
                Sort by:
              </span>
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                    sortBy === option.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Buyback Toggle */}
          {leaderboardData?.pages[0]?.buybackPercentage !== null && leaderboardData?.pages[0]?.buybackPercentage !== undefined && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground mr-2">Display values:</span>
                <button
                  onClick={() => setShowBuyback(true)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border flex items-center gap-1.5",
                    showBuyback
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  <DollarSign className="w-3 h-3" />
                  Buyback ({((leaderboardData?.pages[0]?.buybackPercentage ?? 0) * 100).toFixed(0)}%)
                </button>
                <button
                  onClick={() => setShowBuyback(false)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                    !showBuyback
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  Insured Value
                </button>
              </div>
            </div>
          )}

          {/* Tier Filter */}
          {sortedTiers.length > 0 && (
            <div className={cn(
              "mt-4 pt-4 border-t border-border",
              leaderboardData?.pages[0]?.buybackPercentage === null && "mt-4 pt-4 border-t border-border"
            )}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground mr-2">Filter by tier:</span>
                <button
                  onClick={toggleAllTiers}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                    selectedTierIds.length === 0
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  All Tiers
                </button>
                {sortedTiers.map((tier) => {
                  const isSelected = selectedTierIds.includes(tier.id);
                  return (
                    <button
                      key={tier.id}
                      onClick={() => toggleTier(tier.id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                        isSelected
                          ? "border-transparent text-white"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                      )}
                      style={isSelected ? { backgroundColor: tier.color } : undefined}
                    >
                      {tier.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Info className="w-3 h-3 shrink-0" />
                <span>Cost = total pulls by user on this machine × price. Pulls shows all wins, not just tier wins.</span>
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-secondary rounded-full mb-4">
                <Trophy className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Rankings Yet</h3>
              <p className="text-muted-foreground max-w-md">
                No tier wins have been recorded in the selected time range.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table Layout */}
              <div className="hidden lg:block space-y-2">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 px-4 py-2 text-sm font-medium text-muted-foreground border-b border-border">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-2">Wallet</div>
                  <div className="col-span-1 text-center">Wins</div>
                  <div className="col-span-2 text-right">Cost</div>
                  <div className="col-span-2 text-right">Won</div>
                  <div className="col-span-2 text-right">P/L</div>
                  <div className="col-span-1 text-right">Top</div>
                  <div className="col-span-1 text-right">Last</div>
                </div>

                {/* Entries */}
                {entries.map((entry) => {
                  const pulls = Number(entry.totalPulls) || 0;
                  const cost = machineData ? pulls * machineData.machine.priceUsd : 0;
                  // Use buyback value if toggle enabled and available, otherwise use insured value
                  const totalValue = (showBuyback && entry.totalBuybackValue !== null)
                    ? Number(entry.totalBuybackValue)
                    : Number(entry.totalValue);
                  const profitLoss = totalValue - cost;
                  const roiPercent = cost > 0 ? (profitLoss / cost) * 100 : 0;

                  return (
                    <div
                      key={entry.wallet}
                      className={cn(
                        "grid grid-cols-12 gap-2 px-4 py-3 rounded-lg border border-transparent hover:border-border hover:bg-card/50 transition-colors items-center",
                        getRankStyle(entry.rank)
                      )}
                    >
                      {/* Rank */}
                      <div className="col-span-1 flex items-center gap-2">
                        {getRankIcon(entry.rank) || (
                          <span className="text-muted-foreground font-medium">
                            {entry.rank}
                          </span>
                        )}
                      </div>

                      {/* Wallet */}
                      <div className="col-span-2">
                        <a
                          href={`https://solscan.io/account/${entry.wallet}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-sm hover:text-primary flex items-center gap-1"
                        >
                          {shortenWallet(entry.wallet)}
                          <ExternalLink className="w-3 h-3 opacity-50" />
                        </a>
                      </div>

                      {/* Tier Wins */}
                      <div className="col-span-1 text-center">
                        <span className="font-semibold">{entry.tierWinCount}</span>
                      </div>

                      {/* Cost */}
                      <div className="col-span-2 text-right">
                        {machineData ? (
                          <span className="text-red-400 font-medium">
                            ${cost.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>

                      {/* Total Value Won */}
                      <div className="col-span-2 text-right">
                        <span className="text-emerald-400 font-medium">
                          ${totalValue.toLocaleString()}
                        </span>
                      </div>

                      {/* Profit/Loss */}
                      <div className="col-span-2 text-right">
                        {machineData ? (
                          <div className={cn(
                            "font-medium",
                            profitLoss >= 0 ? "text-emerald-400" : "text-red-400"
                          )}>
                            <span>{profitLoss >= 0 ? "+" : ""}${profitLoss.toLocaleString()}</span>
                            <span className="text-xs ml-1 opacity-70">
                              ({roiPercent >= 0 ? "+" : ""}{roiPercent.toFixed(0)}%)
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>

                      {/* Top Prize */}
                      <div className="col-span-1 text-right">
                        <span className="text-muted-foreground">
                          ${((showBuyback && entry.biggestBuybackWin !== null)
                            ? Number(entry.biggestBuybackWin)
                            : Number(entry.biggestWin)).toLocaleString()}
                        </span>
                      </div>

                      {/* Last Win */}
                      <div className="col-span-1 text-right text-sm text-muted-foreground">
                        {formatTimeAgo(entry.lastWinAt)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile Card Layout */}
              <div className="lg:hidden space-y-3">
                {entries.map((entry) => {
                  const pulls = Number(entry.totalPulls) || 0;
                  const cost = machineData ? pulls * machineData.machine.priceUsd : 0;
                  // Use buyback value if toggle enabled and available, otherwise use insured value
                  const totalValue = (showBuyback && entry.totalBuybackValue !== null)
                    ? Number(entry.totalBuybackValue)
                    : Number(entry.totalValue);
                  const profitLoss = totalValue - cost;
                  const roiPercent = cost > 0 ? (profitLoss / cost) * 100 : 0;

                  return (
                    <div
                      key={entry.wallet}
                      className={cn(
                        "p-4 rounded-lg border transition-colors",
                        getRankStyle(entry.rank) || "border-border"
                      )}
                    >
                      {/* Top row: Rank + Wallet + Wins */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary shrink-0">
                            {getRankIcon(entry.rank) || (
                              <span className="text-sm font-bold text-muted-foreground">
                                {entry.rank}
                              </span>
                            )}
                          </div>
                          <a
                            href={`https://solscan.io/account/${entry.wallet}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm hover:text-primary flex items-center gap-1"
                          >
                            {shortenWallet(entry.wallet)}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold">{entry.tierWinCount}</span>
                          <span className="text-xs text-muted-foreground ml-1">wins</span>
                        </div>
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {/* Cost */}
                        <div>
                          <span className="text-muted-foreground text-xs">Cost</span>
                          <div className="text-red-400 font-medium">
                            {machineData ? `$${cost.toLocaleString()}` : "-"}
                          </div>
                        </div>

                        {/* Won */}
                        <div className="text-right">
                          <span className="text-muted-foreground text-xs">Won</span>
                          <div className="text-emerald-400 font-medium">
                            ${totalValue.toLocaleString()}
                          </div>
                        </div>

                        {/* P/L */}
                        <div>
                          <span className="text-muted-foreground text-xs">P/L</span>
                          {machineData ? (
                            <div className={cn(
                              "font-medium",
                              profitLoss >= 0 ? "text-emerald-400" : "text-red-400"
                            )}>
                              {profitLoss >= 0 ? "+" : ""}${profitLoss.toLocaleString()}
                              <span className="text-xs opacity-70 ml-1">
                                ({roiPercent >= 0 ? "+" : ""}{roiPercent.toFixed(0)}%)
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>

                        {/* Top + Last */}
                        <div className="text-right">
                          <span className="text-muted-foreground text-xs">Top: </span>
                          <span className="text-muted-foreground">
                            ${((showBuyback && entry.biggestBuybackWin !== null)
                              ? Number(entry.biggestBuybackWin)
                              : Number(entry.biggestWin)).toLocaleString()}
                          </span>
                          <span className="text-muted-foreground mx-1">·</span>
                          <span className="text-muted-foreground text-xs">
                            {formatTimeAgo(entry.lastWinAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Load More Button */}
              {hasNextPage && (
                <div className="flex justify-center pt-6">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Load More
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {entries.length > 0 && (() => {
        const combinedWins = entries.reduce((sum, e) => sum + Number(e.tierWinCount), 0);
        // Use buyback values if toggle enabled and available, otherwise use insured values
        const combinedValue = entries.reduce((sum, e) => {
          const value = (showBuyback && e.totalBuybackValue !== null)
            ? Number(e.totalBuybackValue)
            : Number(e.totalValue);
          return sum + value;
        }, 0);
        const combinedPulls = entries.reduce((sum, e) => sum + (Number(e.totalPulls) || 0), 0);
        const combinedCost = machineData ? combinedPulls * machineData.machine.priceUsd : 0;
        const netProfitLoss = combinedValue - combinedCost;

        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            <Card>
              <CardContent className="py-4">
                <p className="text-xs md:text-sm text-muted-foreground">Combined Wins</p>
                <p className="text-xl md:text-2xl font-bold">{combinedWins}</p>
              </CardContent>
            </Card>
            {machineData && (
              <Card>
                <CardContent className="py-4">
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Combined Cost
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-red-400">
                    ${combinedCost.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {combinedPulls.toLocaleString()} pulls
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="py-4">
                <p className="text-xs md:text-sm text-muted-foreground">Combined Value</p>
                <p className="text-xl md:text-2xl font-bold text-emerald-400">
                  ${combinedValue.toLocaleString()}
                </p>
              </CardContent>
            </Card>
            {machineData && (
              <Card>
                <CardContent className="py-4">
                  <p className="text-xs md:text-sm text-muted-foreground">Net Profit/Loss</p>
                  <p className={cn(
                    "text-xl md:text-2xl font-bold",
                    netProfitLoss >= 0 ? "text-emerald-400" : "text-red-400"
                  )}>
                    {netProfitLoss >= 0 ? "+" : ""}${netProfitLoss.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="py-4">
                <p className="text-xs md:text-sm text-muted-foreground">Unique Winners</p>
                <p className="text-xl md:text-2xl font-bold">{leaderboardData?.pages[0]?.totalUniqueWinners ?? 0}</p>
              </CardContent>
            </Card>
          </div>
        );
      })()}
    </div>
  );
}
